/**
 * Global portfolio selector dropdown for the navbar.
 *
 * Shows the currently selected portfolio prominently and allows
 * switching between portfolios. Selection persists across navigation
 * and page refreshes via PortfolioFilterContext.
 */
import { usePortfolioFilter } from '../../contexts'

export function PortfolioSelector() {
  const { selectedPortfolioId, portfolios, selectPortfolio, isLoading } = usePortfolioFilter()

  // Sort portfolios alphabetically
  const sortedPortfolios = [...portfolios].sort((a, b) => a.name.localeCompare(b.name))

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-50 rounded-lg min-w-[140px]">
        <FolderIcon className="h-4 w-4" />
        <span>Loading...</span>
      </div>
    )
  }

  return (
    <div className="relative flex items-center">
      <FolderIcon className="absolute left-2.5 h-4 w-4 text-gray-500 pointer-events-none" />
      <select
        className="appearance-none pl-8 pr-8 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[160px]"
        value={selectedPortfolioId || ''}
        onChange={(e) => selectPortfolio(e.target.value || null)}
      >
        <option value="">All Portfolios</option>
        {sortedPortfolios.map((portfolio) => (
          <option key={portfolio.portfolio_id} value={portfolio.portfolio_id}>
            {portfolio.name}
            {portfolio.holdings_count != null && portfolio.holdings_count > 0
              ? ` (${portfolio.holdings_count})`
              : ''}
          </option>
        ))}
      </select>
      <ChevronIcon className="absolute right-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
    </div>
  )
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  )
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
    </svg>
  )
}
